import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { App } from '@df/core/app';
import { DragScrollDirective } from '@df/ui/common/drag-scroll.directive';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsBlock, NICmsBlock } from '@icms/core/resource/block/icms-block';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { AbstractCmsBlockComponent } from '../../../cms/block/abstract-cms-block.component';
import { CategoryDecorator } from '../../model/category/category.decorator';

interface ICategoryQuickLinksBlockContent extends NICmsContentComponent.IData {
    quick_links?: ICategoryQuickLink[];
}

interface ICategoryQuickLink {
    label: string;
    url: string;
}

export const CATEGORY_QUICK_LINKS_CONTENT_BLOCK_URL = 'category-quick-links-content-block';

@Component({
    selector: 'category-quick-links',
    templateUrl: './category-quick-links.component.html',
    styleUrl: './category-quick-links.component.scss',
    standalone: true,
    imports: [DragScrollDirective, HrefComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<CategoryQuickLinksComponent>({
    id: CATEGORY_QUICK_LINKS_CONTENT_BLOCK_URL,
    block: {
        url: CATEGORY_QUICK_LINKS_CONTENT_BLOCK_URL
    },
    onDemandOnly: true,
    name: 'Category Quick Links',
    description: 'Content block used on the account dashboard',
    fields: [
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'quick_links',
            title: 'Quick Links',
            default: [],
            itemName: 'Quick link',
            itemNameField: 'text',
            fields: [
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'label',
                    title: 'Label',
                    required: true
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'url',
                    title: 'Url',
                    required: true
                }
            ]
        }
    ]
})
export class CategoryQuickLinksComponent extends AbstractCmsBlockComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    override content!: ICategoryQuickLinksBlockContent;

    @Input()
    category?: CategoryDecorator;

    fallbackContent?: ICategoryQuickLinksBlockContent;
    fallbackContentLoaded = false;

    protected iCmsBlockResource = inject(ICmsBlockResource);
    protected app = inject(App);

    protected override canDetach = true;

    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.add(this.app.meta.subscribe(() => this.detectChanges()));
    }

    private getFallbackContent() {
        this.iCmsBlockResource
            .retrieve<ICmsBlock<NICmsBlock.IData<ICategoryQuickLinksBlockContent>>>({
                bareUrl: CATEGORY_QUICK_LINKS_CONTENT_BLOCK_URL,
                variant: this.category?.cmsBlockCategoryQuickLinksVariant
            }, undefined)
            .then((response: ICmsBlock<NICmsBlock.IData<ICategoryQuickLinksBlockContent>> | void) => {
                if (response instanceof Object) {
                    this.fallbackContent = response?.data?.content;
                    this.fallbackContentLoaded = true;

                    this.detectChanges();
                }
            })
            .catch(() => {
                this.fallbackContent = undefined;
                this.fallbackContentLoaded = true;

                this.detectChanges();
            });
    }

    get quickLinks(): ICategoryQuickLink[] {
        if (this.content?.quick_links?.length) {
            return this.content?.quick_links;
        } else if (this.fallbackContent?.quick_links?.length) {
            return this.fallbackContent?.quick_links;
        }

        return [];
    }

    protected _isEmpty(): boolean {
        return !this.content?.quick_links?.length && !this.fallbackContent?.quick_links?.length;
    }

    override detectChanges(): void {
        if (this.initiated) {
            if (this._isEmpty() && !this.fallbackContentLoaded && !this.client.isCms) {
                this.getFallbackContent();
            } else {
                super.detectChanges();
            }
        }
    }
}
